import { useMediaQuery } from "@mui/material";
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from "next";
import Head from "next/head";
// <--- Import Head
import { useRouter } from "next/router";
import { ReactElement, useEffect } from "react";
import { useDispatch } from "react-redux";

// import { toast } from "react-toastify"; // Not used in this snippet, remove if not needed elsewhere
import InnerLayout from "../../components/innerLayout/InnerLayout";
import Loader from "../../components/Loader";
import DesktopView from "../../components/public-profile/DesktopView";
import MobileView from "../../components/public-profile/MobileView";
import RelatedChefs from "../../components/RelatedChefs";
import Tabs from "../../constants/tabs";
import {
  fetchAllChefDynamicUrls,
  fetchImagesFromUid,
  fetchUidFromChefName,
  fetchUserData,
} from "../../firebase/user";
import { setSelectedSidebarUser } from "../../redux/createSidebarSlice";
import { setTab } from "../../redux/createTabSlice";

type ProfilePublicProps = {
  userData: Awaited<ReturnType<typeof fetchUserData>> | null;
  imageData: Awaited<ReturnType<typeof fetchImagesFromUid>> | null;
  chefDynamicUrl: string; // <-- Add this to pass the dynamic URL for canonical
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    console.log("Generating static paths for chef profiles...");
    const allChefUrls = await fetchAllChefDynamicUrls();
    console.log(`Found ${allChefUrls.length} chef URLs to generate paths for`);

    const paths = allChefUrls.map((url) => ({
      params: { "chef-dynamic-url": url.toLowerCase() },
    }));

    console.log(`Generated ${paths.length} static paths`);
    return { paths, fallback: "blocking" };
  } catch (error) {
    console.error("Error fetching paths for chef profiles:", error);
    // Return empty paths but keep fallback blocking to allow dynamic generation
    return { paths: [], fallback: "blocking" };
  }
};

export const getStaticProps: GetStaticProps<
  ProfilePublicProps,
  { "chef-dynamic-url": string }
> = async (context) => {
  const chefDynamicUrl = context.params?.["chef-dynamic-url"];

  if (!chefDynamicUrl || typeof chefDynamicUrl !== "string") {
    console.error("getStaticProps: chefDynamicUrl is missing or invalid");
    return { notFound: true };
  }

  console.log(`getStaticProps: Fetching data for chef: ${chefDynamicUrl}`);
  try {
    const uid = await fetchUidFromChefName(chefDynamicUrl);
    if (!uid) {
      console.warn(`getStaticProps: No UID found for chef: ${chefDynamicUrl}`);
      return { notFound: true };
    }

    console.log(`getStaticProps: Found UID ${uid} for chef: ${chefDynamicUrl}`);

    const [userData, imageData] = await Promise.all([
      fetchUserData(uid),
      fetchImagesFromUid(uid),
    ]);

    if (!userData) {
      console.warn(`getStaticProps: No user data found for UID: ${uid}`);
      return { notFound: true };
    }

    console.log(
      `getStaticProps: Successfully fetched data for chef: ${chefDynamicUrl}`
    );

    return {
      props: {
        userData: userData || null,
        imageData: imageData || null,
        chefDynamicUrl: chefDynamicUrl, // <-- Pass the chefDynamicUrl
      },
      revalidate: 86400,
    };
  } catch (error) {
    console.error(`Error fetching data for chef ${chefDynamicUrl}:`, error);
    return { notFound: true };
  }
};

const ProfilePublic = ({
  userData,
  imageData,
  chefDynamicUrl, // <-- Destructure chefDynamicUrl
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const dispatch = useDispatch();
  const isMdSize = useMediaQuery("(min-width:900px)");
  const router = useRouter();

  useEffect(() => {
    dispatch(setSelectedSidebarUser(userData ?? null));
    dispatch(setTab(Tabs.Search));
    return () => {
      dispatch(setSelectedSidebarUser(null));
    };
  }, [dispatch, userData]);

  if (!userData) {
    // This case should ideally be fully handled by getStaticProps returning notFound: true
    // If it still occurs, it's a loader state or an unexpected issue.
    console.error("ProfilePublic: Rendered without userData.");
    return (
      <>
        <Head>
          <title>Profile Not Found</title>
          <meta name="robots" content="noindex" />
        </Head>
        <Loader />
      </>
    );
  }

  // --- Prepare Meta Tag Content ---
  const chefName =
    userData.publicName ||
    `${userData.firstName || ""} ${userData.lastName || ""}`.trim();

  // Location string for title
  const locationStr = userData.address?.locality
    ? ` in ${userData.address.locality}${
        userData.address.state ? `, ${userData.address.state}` : ""
      }`
    : "";

  // Service types for value proposition
  const primaryService = userData.chefServices?.[0] || "Private Chef";
  const isTopRated = userData.rating && userData.rating >= 4.5;
  const ratingText = isTopRated ? "Top-Rated " : "";

  // Action-oriented, compelling title
  const pageTitle = `Hire ${chefName} - ${ratingText}${primaryService}${locationStr} | MorePleaze`;

  // Compelling meta description with benefits and call-to-action
  const metaDescription =
    userData.bio && userData.bio.length > 20
      ? `Hire ${chefName}, ${ratingText.toLowerCase()}professional chef${locationStr}. ${userData.bio.substring(
          0,
          100
        )}... Book your culinary experience today on MorePleaze!`
      : `Hire ${chefName}, ${ratingText.toLowerCase()}professional chef${locationStr}. ${
          userData.hourlyRate
            ? `Starting at $${userData.hourlyRate}/hour. `
            : ""
        }Book personalized dining experiences, cooking classes & private catering. Available on MorePleaze!`;
  const profileImageUrl = userData.profileImage || "/default-profile-image.png"; // Provide a fallback image
  const canonicalUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/chef/${chefDynamicUrl}`; // Set NEXT_PUBLIC_BASE_URL in .env

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:site_name" content="MorePleaze" />
        {profileImageUrl && (
          <meta property="og:image" content={profileImageUrl} />
        )}
        {profileImageUrl && (
          <meta
            property="og:image:alt"
            content={`${chefName} - Professional Chef`}
          />
        )}
        {userData.hourlyRate && (
          <meta property="og:price:amount" content={userData.hourlyRate} />
        )}
        {userData.hourlyRate && (
          <meta property="og:price:currency" content="USD" />
        )}

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={canonicalUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={metaDescription} />
        {profileImageUrl && (
          <meta property="twitter:image" content={profileImageUrl} />
        )}
        {profileImageUrl && (
          <meta
            property="twitter:image:alt"
            content={`${chefName} - Professional Chef Profile`}
          />
        )}
        <meta property="twitter:creator" content="@MorePleaze" />
        <meta property="twitter:site" content="@MorePleaze" />
        {/* <meta name="twitter:site" content="@YourTwitterHandle" /> */}
        {/* <meta name="twitter:creator" content="@ChefTwitterHandleIfAvailable" /> */}
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        {/* Additional Meta Tags (Optional) */}
        <meta
          name="keywords"
          content={`hire chef, ${chefName}, private chef${locationStr.replace(
            " in ",
            " "
          )}, ${primaryService.toLowerCase()}, personal chef, cooking classes, catering, MorePleaze${
            userData.chefServices
              ? `, ${userData.chefServices.join(", ").toLowerCase()}`
              : ""
          }`}
        />
        <meta name="author" content={chefName} />

        {/* Additional CTR-boosting meta tags */}
        {isTopRated && (
          <meta name="rating" content={userData.rating?.toString()} />
        )}
        {userData.hourlyRate && (
          <meta name="price" content={`$${userData.hourlyRate}/hour`} />
        )}
        <meta name="availability" content="Available for booking" />
        <meta
          name="service-area"
          content={userData.address?.locality || "Multiple locations"}
        />
        {/* Schema.org Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Person",
              name: chefName,
              image: profileImageUrl,
              description: metaDescription,
              url: canonicalUrl,
              jobTitle: primaryService,
              alternateName: userData.publicName
                ? [
                    userData.publicName,
                    `${userData.firstName} ${userData.lastName}`,
                  ].filter(Boolean)
                : undefined,
              worksFor: {
                "@type": "Organization",
                name: "MorePleaze",
                url: "https://app.morepleaze.com",
              },
              hasOccupation: {
                "@type": "Occupation",
                name: primaryService,
                description: `Professional ${primaryService.toLowerCase()} offering personalized culinary experiences`,
              },
              address: userData.address
                ? {
                    "@type": "PostalAddress",
                    addressLocality: userData.address.locality || "",
                    postalCode: userData.address.postalCode || "",
                    addressRegion: userData.address.state || "",
                    addressCountry: userData.address.country || "US",
                  }
                : undefined,
              aggregateRating: userData.rating
                ? {
                    "@type": "AggregateRating",
                    ratingValue: userData.rating,
                    ratingCount: userData.ratingCount || 1,
                  }
                : undefined,
              knowsAbout: userData.chefServices || [],
              priceRange: userData.hourlyRate
                ? `$${userData.hourlyRate}/hour`
                : undefined,
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Culinary Services",
                itemListElement: userData.chefServices?.map((service) => ({
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: service,
                    description: `Professional ${service.toLowerCase()} services by ${chefName}`,
                  },
                  priceSpecification: userData.hourlyRate
                    ? {
                        "@type": "PriceSpecification",
                        price: userData.hourlyRate,
                        priceCurrency: "USD",
                        unitText: "hour",
                      }
                    : undefined,
                })) || [
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Private Chef Services",
                      description: `Professional private chef services by ${chefName}`,
                    },
                  },
                ],
              },
              makesOffer: userData.hourlyRate
                ? {
                    "@type": "Offer",
                    name: `${primaryService} Services`,
                    description: `Hire ${chefName} for personalized culinary experiences`,
                    priceSpecification: {
                      "@type": "PriceSpecification",
                      price: userData.hourlyRate,
                      priceCurrency: "USD",
                      unitText: "hour",
                    },
                    availability: "https://schema.org/InStock",
                    validFrom: new Date().toISOString(),
                  }
                : undefined,
              sameAs: [
                userData.instagram,
                userData.facebook,
                userData.linkedIn,
                userData.website,
              ].filter(Boolean),
            }),
          }}
        />
      </Head>

      {isMdSize ? (
        <DesktopView userData={userData} imageData={imageData || undefined} />
      ) : (
        <MobileView userData={userData} imageData={imageData || []} />
      )}

      {/* Related Chefs for better internal linking and SEO */}
      {/* 
      <RelatedChefs
        currentChefUid={userData.uid}
        currentLocation={
          userData.address
            ? {
                lat: userData.address.lat,
                lng: userData.address.lng,
              }
            : undefined
        }
        limit={6}
      />
      */}
    </>
  );
};

ProfilePublic.getLayout = function getLayout(component: ReactElement) {
  return <InnerLayout>{component}</InnerLayout>;
};

export default ProfilePublic;
